import type { Person } from '@sd/contracts';
import { useMemo, useState } from 'react';
import { Icon } from '../../icons/Icon';
import { Avatar } from '../../ui/Avatar';
import { Button, Field, Notice, Tag } from '../../ui/controls';
import { DialogHead, Modal, useToast } from '../../ui/overlays';
import { useAppEnv, useClient, useClientState } from '../context';
import { errorMessage } from '../../errors';
import { isSelfChat } from '../copy';
import { useShell } from '../shell';

/** "새 대화" / "멤버 초대" / "대화 상대 추가" people picker (design m-people). */
export function PeopleModal({ mode, preselect }: { mode: 'new' | 'add-member' | 'add-peer'; preselect?: string[] }) {
  const env = useAppEnv();
  const shell = useShell();
  const client = useClient();
  const toast = useToast();
  const people = useClientState((s) => s.people);
  // Without mail an invitation is a link someone hands over, not an email (ADR-0007).
  const mailOff = useClientState((s) => s.serverInfo?.mail.status === 'not_configured');
  const meId = useClientState((s) => s.me?.id ?? '');
  const conversations = useClientState((s) => s.conversations);
  const conversation = useClientState((s) => (s.activeConversationId ? s.conversations[s.activeConversationId] : undefined));
  const details = useClientState((s) => (s.activeConversationId ? s.details[s.activeConversationId] : undefined));
  const peerId = mode === 'add-peer' ? conversation?.peer?.id : undefined;
  const [selected, setSelected] = useState<string[]>(() => (peerId ? [peerId] : (preselect ?? [])));
  const [q, setQ] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const pool = useMemo(() => Object.values(people).filter((p) => p.id !== meId), [people, meId]);
  const dupNames = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of pool) counts[p.displayName] = (counts[p.displayName] ?? 0) + 1;
    return counts;
  }, [pool]);
  const memberIds = useMemo(() => new Set(mode === 'add-member' ? (details?.members.map((m) => m.person.id) ?? []) : []), [mode, details]);
  const query = q.trim();
  const results = pool
    .filter((p) => !query || p.displayName.includes(query) || p.department.includes(query) || p.email.includes(query.toLowerCase()))
    .sort((a, b) => Number(a.status === 'deactivated') - Number(b.status === 'deactivated') || a.displayName.localeCompare(b.displayName, 'ko'));
  const toggle = (p: Person) => {
    if (p.status === 'deactivated' || memberIds.has(p.id) || p.id === peerId) return;
    setSelected((sel) => (sel.includes(p.id) ? sel.filter((x) => x !== p.id) : [...sel, p.id]));
  };
  const n = selected.length;
  const existingDirect = n === 1 && mode === 'new' ? Object.values(conversations).find((c) => c.kind === 'direct' && c.peer?.id === selected[0]) : undefined;

  // "나와의 채팅": a conversation with oneself for notes and files, opened at once rather than picked like a person.
  const me = people[meId];
  const selfChat = Object.values(conversations).find(isSelfChat);
  const offerSelf = mode === 'new' && me && (!query || '나와의 채팅'.includes(query) || me.displayName.includes(query));
  const openSelf = async () => {
    setBusy(true);
    try {
      shell.openConversation(selfChat?.id ?? (await client.openDirect(meId)));
      env.closeModal();
    } catch (error) {
      toast(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const go = async () => {
    setBusy(true);
    try {
      if (mode === 'add-member' && conversation) {
        await client.addMembers(conversation.id, selected);
        toast(`${n}명을 그룹에 초대했어요`);
      } else if (mode === 'new' && n === 1) {
        const id = existingDirect?.id ?? (await client.openDirect(selected[0]!));
        shell.openConversation(id);
      } else {
        const id = await client.createGroup({ memberIds: selected, ...(title.trim() ? { title: title.trim() } : {}), ...(mode === 'add-peer' && conversation ? { fromDirectId: conversation.id } : {}) });
        shell.openConversation(id);
        toast('새 그룹을 만들었어요');
      }
      env.closeModal();
    } catch (error) {
      toast(errorMessage(error, { ALREADY_MEMBER: '이미 참여 중인 사람이에요' }));
    } finally {
      setBusy(false);
    }
  };

  const heading = { new: '새 대화', 'add-member': '멤버 초대', 'add-peer': '대화 상대 추가' }[mode];
  let action: React.ReactNode;
  let grow: React.ReactNode = <span className="grow" />;
  if (mode === 'add-member') {
    action = (
      <Button variant="primary" disabled={!n || busy} onClick={() => void go()}>
        {n ? `${n}명 초대` : '초대할 사람 선택'}
      </Button>
    );
  } else if (n === 0) {
    action = (
      <Button variant="primary" disabled>
        대화할 사람을 선택하세요
      </Button>
    );
  } else if (n === 1 && mode === 'new') {
    grow = <span className="grow">{existingDirect ? '이미 1:1 대화가 있어요 — 기존 대화를 열어요' : ''}</span>;
    action = (
      <Button variant="primary" disabled={busy} onClick={() => void go()}>
        {existingDirect ? '기존 대화 열기' : '1:1 대화 시작'}
      </Button>
    );
  } else {
    grow = <input className="field" style={{ height: 40, flex: 1, minWidth: 160 }} placeholder="그룹 이름 (선택)" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="그룹 이름" maxLength={100} />;
    action = (
      <Button variant="primary" disabled={n < 2 || busy} onClick={() => void go()}>
        {mode === 'add-peer' && n < 2 ? '한 명 이상 더 선택' : `그룹 만들기 · ${n + 1}명`}
      </Button>
    );
  }

  return (
    <Modal open onClose={env.closeModal} labelledBy="mp-t" id="m-people">
      <DialogHead titleId="mp-t" title={heading} onClose={env.closeModal} />
      <div className="dlg-body scroll">
        <Field icon="search" placeholder="이름, 부서, 이메일로 찾기" aria-label="사람 검색" value={q} onChange={(e) => setQ(e.target.value)} data-autofocus />
        <div className="pp-chips">
          {selected.map((id) => {
            const p = people[id];
            if (!p) return null;
            return (
              <span key={id} className="pp-chip">
                <Avatar person={p} noDot />
                <span className="ell">{p.displayName}</span>
                {id === peerId ? null : (
                  <button type="button" onClick={() => setSelected((sel) => sel.filter((x) => x !== id))} aria-label={`${p.displayName} 선택 해제`}>
                    <Icon name="x" size="xs" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
        {mode === 'add-member' ? (
          <Notice icon="history">
            현재 이 그룹은 <b>{conversation?.historyMode === 'include_history' ? '기존 내역 포함' : '참여 이후 메시지만'}</b> 보여요.{' '}
            {conversation?.historyMode === 'include_history' ? '새 멤버도 보존된 이전 대화를 볼 수 있어요.' : '새 멤버는 초대된 뒤의 대화부터 볼 수 있어요.'}
          </Notice>
        ) : mode === 'add-peer' ? (
          <Notice icon="info">새 그룹 대화가 만들어져요. {conversation?.peer?.displayName ?? ''}님과의 기존 1:1 내역은 공유되지 않아요.</Notice>
        ) : null}
        <div className="plist">
          {offerSelf ? (
            <button type="button" className="prow self" disabled={busy} onClick={() => void openSelf()}>
              <Avatar person={me} noDot />
              <span className="pr-main">
                <span className="pr-name">
                  나와의 채팅
                  <Tag>나</Tag>
                </span>
                <span className="pr-sub ell">메모나 파일을 나에게 보내 두고 다른 기기에서 봐요</span>
              </span>
              <Icon name="right" size="sm" />
            </button>
          ) : null}
          {results.length ? (
            results.map((p) => {
              const inGroup = memberIds.has(p.id);
              const disabled = p.status === 'deactivated' || inGroup;
              const sel = selected.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`prow${sel ? ' sel' : ''}${disabled ? ' disabled' : ''}`}
                  aria-disabled={disabled || undefined}
                  aria-pressed={sel}
                  onClick={() => toggle(p)}
                >
                  <Avatar person={p} />
                  <span className="pr-main">
                    <span className="pr-name">
                      {p.displayName}
                      {(dupNames[p.displayName] ?? 0) > 1 ? <Tag tone="warn">동명이인</Tag> : null}
                      {p.status === 'deactivated' ? <Tag>비활성화</Tag> : null}
                      {inGroup ? <Tag>이미 참여 중</Tag> : null}
                    </span>
                    <span className="pr-sub ell">
                      {p.department} · {p.email}
                    </span>
                  </span>
                  <span className="ck">{sel ? <Icon name="check" size="xs" /> : null}</span>
                </button>
              );
            })
          ) : (
            <div className="empty-note">
              ‘{query}’에 해당하는 사람이 없어요
              <br />
              <Button size="sm" variant="primary" icon={mailOff ? 'userplus' : 'mail'} style={{ marginTop: 12 }} onClick={() => env.openModal({ kind: 'invite' })}>
                {mailOff ? '직원 초대하기' : '이메일로 초대하기'}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="dlg-foot">
        {grow}
        <Button variant="ghost" onClick={env.closeModal}>
          취소
        </Button>
        {action}
      </div>
    </Modal>
  );
}
